/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Inquiry info page custom attributes block
 *
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.15.0
 */

import { useCallback } from 'react';

import moment from 'moment';
import { useSelector } from 'react-redux';

import { Checkbox } from '@components/common';
import { getTimeZone } from '@components/Table/helpers/getTimeZone';
import type { IPageInquiryInfoTranslation } from '@modules/internationalization/types/pages/inquiryInfo';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { getPrintableText } from '@utils/formatText';

import { makeSelectIsFeatureWithVersionEnabled } from '../../PageAuth/selectors/authSelectors';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import {
    inquiryInfoApiReportNoteSelector,
    inquiryInfoApiVisibleInquiryAttributesSelector,
} from '../selectors';
import { EDataType, HIDDEN_VISIBILITY, IInquiryAttributeResponseData } from '../types/inquiryInfoType';
import { InquiryInfoPageStyled } from './styled/InquiryInfoPageStyled';

export interface IInquiryInfoPageCustomAttributes {
    inquiryId: string;
    translationInquiryInfo: IPageInquiryInfoTranslation;
}

const InquiryInfoPageCustomAttributes = (props: IInquiryInfoPageCustomAttributes) => {
    const {
        inquiryId,
        translationInquiryInfo,
    } = props;

    const reportNote = useSelector(inquiryInfoApiReportNoteSelector(inquiryId));
    const visibleInquiryAttributes = useSelector(inquiryInfoApiVisibleInquiryAttributesSelector(inquiryId));
    const isInquiriesV3Enabled = useSelector(makeSelectIsFeatureWithVersionEnabled(
        FeatureNameEnum.INQUIRIES, 3));

    const getCustomAttribute = useCallback((item: IInquiryAttributeResponseData) => {
        switch (item.declaration.dataType) {
            case EDataType.BOOL:
                return <Checkbox
                    checked={item.valueList[0]?.value === '1'}
                    className="inquiry-info-page__text"
                    disabled={true}
                />;
            case EDataType.DATE:
                return item.valueList?.length > 0
                    ? <div>
                        {moment(item.valueList[0].value).format(EFormatData.FullDateWithTime)}&nbsp;
                        {getTimeZone(item.valueList[0].value)}
                    </div>
                    : translationInquiryInfo.labelUndefined
            case EDataType.DICTIONARY:
                return item.valueList?.length > 0
                    ? item.valueList.map(i => i.value.toString()).join(', ')
                    : translationInquiryInfo.labelUndefined
            default:
                return item.valueList?.length > 0
                    ? item.valueList[0].value
                    : translationInquiryInfo.labelUndefined;
        }
    }, [translationInquiryInfo]);

    return (
        <InquiryInfoPageStyled>
            {/* Отчет об обработке */}
            {
                reportNote && <div>
                    <div className="inquiry-info-page__subtitle">
                        {translationInquiryInfo.reportNote}
                    </div>
                    <div className="inquiry-info-page__text"
                        dangerouslySetInnerHTML={({ __html: getPrintableText(reportNote) })}
                    />
                </div>
            }
            {
                !isInquiriesV3Enabled && visibleInquiryAttributes.length > 0 &&
                visibleInquiryAttributes.map((item, index) => {
                    if (HIDDEN_VISIBILITY !== item.declaration.visibility) {
                        return <div key={item.key + index}>
                            <div className="inquiry-info-page__subtitle">{item.name}</div>
                            <div className="inquiry-info-page__text">{getCustomAttribute(item)}</div>
                        </div>
                    }
                    return null
                })
            }
        </InquiryInfoPageStyled>
    );
};

export default InquiryInfoPageCustomAttributes;
